import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { HardHat, Plus, Loader2, Package, Send, PackageCheck, BadgeCheck } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';

interface ExtrasType {
    id: string;
    label: string;
}

interface ExtrasPartner {
    id: string;
    name: string;
    email: string;
    phone: string;
    address: string;
    notes: string;
    logo_url?: string | null;
    types: ExtrasType[];
    request_counts?: { to_request: number; requested: number; confirmed: number };
}

function CreatePartnerModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
    const [name, setName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [address, setAddress] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const handleSave = async () => {
        if (!name.trim()) { setError('Le nom est requis.'); return; }
        setSaving(true);
        setError('');
        try {
            const res: any = await moduleApi.createExtrasPartner({ name: name.trim(), email: email.trim(), phone: phone.trim(), address: address.trim() });
            onCreated(res.id);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de la création.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            title="Nouveau partenaire"
            maxWidth="max-w-md"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Annuler</Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {saving ? 'Création...' : 'Créer'}
                    </Button>
                </>
            }
        >
            <div className="space-y-4">
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Nom *</label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: La Belle Équipe" autoFocus />
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Email</label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@partenaire.fr" />
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Téléphone</label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Adresse</label>
                    <Input value={address} onChange={(e) => setAddress(e.target.value)} />
                </div>
                <p className="text-xs text-content-muted italic">Vous pourrez compléter la fiche (notes, logo, catalogue de types d'extras) une fois le partenaire créé.</p>
                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
            </div>
        </Modal>
    );
}

export const ExtrasPartnersModule: React.FC = () => {
    const navigate = useNavigate();
    const [partners, setPartners] = React.useState<ExtrasPartner[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [showCreate, setShowCreate] = React.useState(false);

    React.useEffect(() => {
        moduleApi.getExtrasPartners().then((d: any) => setPartners(Array.isArray(d) ? d : [])).catch(() => setPartners([])).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="space-y-6">
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-xl font-bold text-content tracking-tight flex items-center gap-2"><HardHat size={18} /> Partenaires Extras</h1>
                    <p className="text-content-muted text-sm mt-0.5">Vos partenaires qui fournissent du personnel externe (agents de sécurité, hôtesses...) pour vos privatisations.</p>
                </div>
                <Button onClick={() => setShowCreate(true)}>
                    <Plus size={15} /> Nouveau partenaire
                </Button>
            </header>

            {partners.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl">
                    <HardHat size={28} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucun partenaire pour l'instant.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {partners.map((p) => (
                        <motion.div
                            key={p.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-surface rounded-2xl border border-border p-6 hover:border-white/20 transition-all cursor-pointer group relative overflow-hidden"
                            onClick={() => navigate(`/extras-partenaires/${p.id}`)}
                        >
                            <div className="relative z-10">
                                <div className="flex justify-between items-start mb-6">
                                    <div className="w-12 h-12 bg-surface-soft rounded-xl flex items-center justify-center border border-border overflow-hidden">
                                        {p.logo_url ? (
                                            <img src={resolveLogoUrl(p.logo_url)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <HardHat className="text-content-muted" size={22} />
                                        )}
                                    </div>
                                    <span className="text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center gap-1">
                                        <Package size={11} /> {p.types.length} type{p.types.length > 1 ? 's' : ''}
                                    </span>
                                </div>
                                <h3 className="text-xl font-bold text-content mb-1 group-hover:text-brand-600 dark:text-brand-400 transition-colors truncate">{p.name}</h3>
                                <p className="text-content-muted text-sm truncate">
                                    {[p.email, p.phone].filter(Boolean).join(' · ') || 'Aucun contact renseigné'}
                                </p>
                                <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-2 text-center">
                                    <div>
                                        <p className="text-sm font-bold text-content flex items-center justify-center gap-1"><Send size={12} className="text-content-muted" /> {p.request_counts?.to_request ?? 0}</p>
                                        <p className="text-[9px] text-content-muted mt-0.5 uppercase tracking-wide">À demander</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-content flex items-center justify-center gap-1"><PackageCheck size={12} className="text-content-muted" /> {p.request_counts?.requested ?? 0}</p>
                                        <p className="text-[9px] text-content-muted mt-0.5 uppercase tracking-wide">Demandées</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-content flex items-center justify-center gap-1"><BadgeCheck size={12} className="text-content-muted" /> {p.request_counts?.confirmed ?? 0}</p>
                                        <p className="text-[9px] text-content-muted mt-0.5 uppercase tracking-wide">Confirmées</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {showCreate && (
                <CreatePartnerModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(id) => navigate(`/extras-partenaires/${id}`)}
                />
            )}
        </div>
    );
};
